import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
 
/**
 * Declaración global de la función `gtag` expuesta por Google Analytics.
 * Se asume que el snippet de GA4 ya fue cargado en `index.html`.
 */
declare var gtag: Function;
 
/**
 * Tipado base flexible y extensible para los parámetros de eventos.
 * Permite enviar cualquier estructura de datos sin imponer un esquema fijo.
 */
export type GAEventParams = {
  [key: string]: any;
};
 
@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
 
  /**
   * Constructor del servicio.
   * Inicializa Google Analytics al momento de cargar la aplicación.
   * El servicio se instancia una sola vez gracias al scope `root`.
   */
  constructor() {
    this.initGA(environment.ga_measurement_id_oficina_virtual);
  }
 
  /**
   * Inicializa Google Analytics 4 usando el Measurement ID configurado
   * en los archivos de entorno.
   *
   * - Evita la inicialización si el Measurement ID no está definido.
   * - Desactiva el envío automático de `page_view` para SPAs.
   * - Activa `debug_mode` solo si el entorno lo indica.
   */
  private initGA(id: string): void {
    if (!id || id.trim() === '') {
      console.warn('[GoogleAnalyticsService] Inicialización de GA omitida: ID no configurado');
      return;
    }

    const gtagFn = (globalThis as any).gtag;
    if (!gtagFn) {
      // En tests o contextos sin navegador, omitir configuración de GA
      return;
    }
    
    gtagFn('js', new Date());
 
    // Configura GA4 y desactiva page_view automático para SPA
    gtagFn('config', id, {
      send_page_view: false,
      transport_type: 'beacon',
      anonymize_ip: true,
    });
  }
 
  /**
   * Envía manualmente un evento `page_view`.
   * Debe ejecutarse cuando Angular cambia de ruta
   * (por ejemplo, en Router NavigationEnd).
   *
   * @param path Ruta actual de la aplicación (ej: '/dashboard')
   * @param params Parámetros adicionales opcionales para el page view
   */
  trackPageView(path: string, params: GAEventParams = {}) {
    const gtagFn = (globalThis as any).gtag;
    if (!gtagFn) return;
    
    gtagFn('event', 'page_view', {
      page_path: path,
      ...params,
    });
  }
 
  /**
   * Envía un evento personalizado a Google Analytics.
   *
   * El nombre del evento debe cumplir las reglas de GA4:
   * - minúsculas
   * - sin espacios
   * - usar snake_case
   *
   * @param eventName Nombre del evento (ej: 'open_modal')
   * @param params Objeto de parámetros completamente libre y extensible
   */
  trackEvent(eventName: string, params: GAEventParams = {}) {
    const gtagFn = (globalThis as any).gtag;
    if (!gtagFn) return;

    const storage_keys = JSON.parse(sessionStorage.getItem('MY_STORAGE_KEY') || '{}');

    const eventParams = {
      client_code: storage_keys.clientCode,
      ...params 
    };

    gtagFn('event', eventName, eventParams);
  }
}